// Removes embedded metadata from an image before it is uploaded, so a shelfie's
// GPS coordinates never leave the shopper's device. Re-drawing through a canvas
// keeps only pixels: EXIF, XMP, and IPTC blocks are not carried over.
//
// The server strips again after upload. This pass exists so the coordinates are
// gone before the bytes travel, and so an object never sits in a public bucket
// with location data on it, however briefly.

const STRIPPABLE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/avif",
  "image/heic",
  "image/heif",
  "image/tiff",
]);

// Formats a canvas can re-encode directly; everything else becomes JPEG.
const PRESERVED_OUTPUT_TYPES: Record<string, string> = {
  "image/png": "image/png",
  "image/webp": "image/webp",
};

const EXTENSION_FOR_TYPE: Record<string, string> = {
  "image/png": "png",
  "image/webp": "webp",
  "image/jpeg": "jpg",
};

const renameForOutputType = (fileName: string, outputType: string): string => {
  const extension = EXTENSION_FOR_TYPE[outputType] ?? "jpg";
  const base = fileName.replace(/\.[^./\\]+$/, "") || "upload";
  return `${base}.${extension}`;
};

export const canStripImageMetadata = (file: File): boolean =>
  STRIPPABLE_TYPES.has(file.type.toLowerCase());

export async function stripImageMetadataInBrowser(file: File): Promise<File> {
  if (
    typeof window === "undefined" ||
    typeof createImageBitmap !== "function" ||
    !canStripImageMetadata(file)
  ) {
    return file;
  }

  let bitmap: ImageBitmap | undefined;
  try {
    // `from-image` bakes the EXIF rotation into the pixels, so dropping the
    // orientation tag cannot leave the photo sideways. The cast is for the DOM
    // lib shipped with this TypeScript version, which still types
    // ImageOrientation as "none" | "flipY"; browsers have accepted
    // "from-image" since Chrome 79, Firefox 77, and Safari 14.
    const bitmapOptions = {
      imageOrientation: "from-image",
    } as unknown as ImageBitmapOptions;
    bitmap = await createImageBitmap(file, bitmapOptions);

    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    if (!context || !canvas.width || !canvas.height) {
      return file;
    }
    context.drawImage(bitmap, 0, 0);

    const outputType =
      PRESERVED_OUTPUT_TYPES[file.type.toLowerCase()] ?? "image/jpeg";
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((result) => resolve(result), outputType, 0.92);
    });
    if (!blob || blob.size === 0) {
      return file;
    }

    return new File([blob], renameForOutputType(file.name, outputType), {
      type: outputType,
      lastModified: Date.now(),
    });
  } catch {
    // Never block an upload on this: the server strips before the image is
    // attached to a post, and refuses anything it cannot clean.
    return file;
  } finally {
    bitmap?.close?.();
  }
}
